'use client';

/**
 * InventoryTrapChart — self-contained, typed React component (TypeScript).
 * Next.js-ready: client component, zero runtime deps, scoped class names (.itc-*).
 *
 * No props. Injects its own scoped styles once, on first mount. Uses the app's
 * Poppins font (already loaded globally) — no font <link> injection.
 * Responsive via CSS container queries so it reflows to its container's width,
 * not the viewport. The three T-accounts scroll horizontally at every width so
 * each panel always keeps enough room.
 */
import React, { useEffect, useRef, useState } from 'react';

/* ---------------------------- Brand assets ----------------------------- */
const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Types -------------------------------- */
type Mode = 'linked' | 'standalone';

interface PO { id: string; amount: number; }
interface Entry { label: string; amount: number; kind: 'receipt' | 'bill'; }
interface Ledger { dr: Entry[]; cr: Entry[]; }

/* -------------------------------- Data --------------------------------- */
const POS: PO[] = [
  { id: 'PO-1001', amount: 5000 },
  { id: 'PO-1002', amount: 3200 },
  { id: 'PO-1003', amount: 8750 },
  { id: 'PO-1004', amount: 2400 },
  { id: 'PO-1005', amount: 6100 },
];

const fmt = (n: number): string => '$' + n.toLocaleString('en-US');

/** Build the three ledgers for the first `step` POs under a given mode. */
function buildLedgers(step: number, mode: Mode): { inv: Ledger; acc: Ledger; ap: Ledger } {
  const inv: Ledger = { dr: [], cr: [] };
  const acc: Ledger = { dr: [], cr: [] };
  const ap: Ledger = { dr: [], cr: [] };
  POS.slice(0, step).forEach((po, i) => {
    const r = `Receipt ${i + 1}`;
    const b = `Bill ${i + 1}`;
    // Item Receipt: Dr Inventory / Cr Accrued Purchases (both modes)
    inv.dr.push({ label: r, amount: po.amount, kind: 'receipt' });
    acc.cr.push({ label: r, amount: po.amount, kind: 'receipt' });
    if (mode === 'linked') {
      // Linked Vendor Bill: Dr Accrued Purchases / Cr AP
      acc.dr.push({ label: b, amount: po.amount, kind: 'bill' });
      ap.cr.push({ label: b, amount: po.amount, kind: 'bill' });
    } else {
      // Standalone Bill: Dr Inventory (again!) / Cr AP
      inv.dr.push({ label: b, amount: po.amount, kind: 'bill' });
      ap.cr.push({ label: b, amount: po.amount, kind: 'bill' });
    }
  });
  return { inv, acc, ap };
}

const sum = (es: Entry[]): number => es.reduce((t, e) => t + e.amount, 0);

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'itc-styles';
const CSS = `
.itc-wrap { container-type: inline-size; width: 100%; }
.itc-root {
  --itc-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --line-2: #EEF1F2; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0F9993; --teal-tint: #ECFBF9; --teal-line: #CDEAE6;
  --gold: #A8842F; --gold-tint: #F6EFDC; --gold-line: #E7DBB8;
  --red: #D92B03; --red-tint: #FDECE7; --red-line: #F6D2C6;
  position: relative;
  box-sizing: border-box; width: 100%; max-width: 1160px; margin: 0 auto;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  --pad: 44px;
  padding: 40px var(--pad) 30px; color: var(--ink);
  font-family: var(--itc-font); -webkit-font-smoothing: antialiased;
}
.itc-root *, .itc-root *::before, .itc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.itc-root h1, .itc-root h2, .itc-root h3, .itc-root p, .itc-root img { margin: 0; }
.itc-root p { letter-spacing: normal; }
/* Isolate from .blog-content prose styles that out-specify the universal reset: leaked h2 margins, the gold ::after underline, and p margins/letter-spacing. */
.itc-root h1, .itc-root h2, .itc-root h3, .itc-root h4, .itc-root p { margin: 0; padding: 0; }
.itc-root p { letter-spacing: normal; }
.itc-root h1::after, .itc-root h2::after, .itc-root h3::after, .itc-root h1::before, .itc-root h2::before, .itc-root h3::before { content: none !important; display: none !important; }

/* PS Global logo — always pinned to the top-right corner, never in flex flow */
.itc-root .itc-logo { position: absolute; top: 34px; right: 44px; height: 34px; width: auto; z-index: 2; }
.itc-root .head { max-width: 60%; margin-top: 6px; }
.itc-root .head h2 { font-size: 32px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.2; }
.itc-root .sub { font-size: 14px; color: var(--body); font-style: italic; margin-top: 10px; }

.itc-root .toggle { flex: none; display: inline-flex; margin-top: 22px; border: 1px solid var(--line); border-radius: 999px; padding: 4px; background: var(--wash); }
.itc-root .toggle button { font-family: var(--itc-font); font-size: 13px; font-weight: 500; color: var(--body); background: transparent; border: 0; border-radius: 999px; padding: 9px 18px; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.18s ease; }
.itc-root .toggle button .dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; opacity: 0.45; }
.itc-root .toggle button.on { background: var(--paper); font-weight: 600; box-shadow: 0 2px 8px -3px rgba(27,42,51,0.25); }
.itc-root .toggle button.on .dot { opacity: 1; }
.itc-root .toggle button.on.good { color: var(--teal); border: 1px solid var(--teal-line); }
.itc-root .toggle button.on.bad { color: var(--red); border: 1px solid var(--red-line); }

/* ---- stepper rail ---- */
.itc-root .rail { display: flex; align-items: flex-start; gap: 18px 28px; margin-top: 32px; flex-wrap: wrap; }
.itc-root .rail-actions { flex: none; display: flex; flex-direction: column; align-items: flex-start; gap: 12px; }
.itc-root .rail .btn { font-family: var(--itc-font); font-size: 14.5px; font-weight: 600; color: #fff; background: var(--teal); border: 0; border-radius: 999px; padding: 13px 24px; min-height: 44px; cursor: pointer; display: inline-flex; align-items: center; gap: 10px; transition: background 0.15s ease, opacity 0.15s ease, transform 0.15s ease; box-shadow: 0 8px 20px -8px rgba(15,153,147,0.6); animation: itc-pulse 1.8s ease-in-out infinite; }
.itc-root .rail .btn .tap { font-size: 16px; line-height: 1; animation: itc-tap 1.8s ease-in-out infinite; }
.itc-root .rail .btn:hover { background: #0C7D77; transform: translateY(-1px); }
.itc-root .rail .btn:disabled { opacity: 0.35; cursor: default; background: var(--ink); box-shadow: none; animation: none; transform: none; }
@keyframes itc-pulse { 0%, 100% { box-shadow: 0 8px 20px -8px rgba(15,153,147,0.6), 0 0 0 0 rgba(15,153,147,0.45); } 50% { box-shadow: 0 8px 20px -8px rgba(15,153,147,0.6), 0 0 0 9px rgba(15,153,147,0); } }
@keyframes itc-tap { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(3px); } }
@media (prefers-reduced-motion: reduce) { .itc-root .rail .btn, .itc-root .rail .btn .tap { animation: none; } }
.itc-root .rail .reset { font-family: var(--itc-font); font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); background: transparent; border: 0; cursor: pointer; padding: 4px 6px; }
.itc-root .rail .reset:hover { color: var(--ink-2); }
.itc-root .pos { display: flex; gap: 7px; flex-wrap: wrap; margin-left: auto; }
.itc-root .po { font-family: var(--itc-font); font-size: 9.5px; font-weight: 600; letter-spacing: 0.04em; border: 1px solid var(--line); border-radius: 5px; padding: 6px 9px; color: var(--muted); background: var(--paper); transition: all 0.2s ease; }
.itc-root .po b { display: block; font-size: 10px; margin-top: 2px; font-weight: 600; }
.itc-root .po.done { color: var(--ink-2); border-color: #C9D6D4; background: var(--wash); }
.itc-root .po.done b { color: var(--ink); }

/* ---- T-accounts (horizontal scroll at every width) ---- */
.itc-root .taccts-wrap { position: relative; margin-top: 28px; margin-left: calc(-1 * var(--pad)); margin-right: calc(-1 * var(--pad)); }
.itc-root .taccts-wrap::before, .itc-root .taccts-wrap::after { content: ''; position: absolute; top: 0; bottom: 8px; width: 40px; pointer-events: none; z-index: 4; opacity: 0; transition: opacity 0.25s ease; }
.itc-root .taccts-wrap::before { left: 0; background: linear-gradient(to right, var(--paper), rgba(255,255,255,0)); }
.itc-root .taccts-wrap::after { right: 0; background: linear-gradient(to left, var(--paper), rgba(255,255,255,0)); }
.itc-root .taccts-wrap.can-l::before { opacity: 1; }
.itc-root .taccts-wrap.can-r::after { opacity: 1; }
.itc-root .taccts { display: flex; gap: 22px; align-items: stretch; overflow-x: auto; scroll-snap-type: x proximity; scroll-padding-left: var(--pad); -webkit-overflow-scrolling: touch; padding: 0 var(--pad) 6px; scrollbar-width: none; -ms-overflow-style: none; }
.itc-root .taccts::-webkit-scrollbar { display: none; }
.itc-root .tacct { flex: 1 0 300px; scroll-snap-align: start; border: 1px solid var(--line); border-radius: 10px; background: var(--paper); overflow: hidden; display: flex; flex-direction: column; }
.itc-root .tacct.star { flex: 1.15 0 330px; }
.itc-root .tacct .cols { flex: 1; }
.itc-root .tacct.star { border-color: var(--gold-line); box-shadow: 0 14px 30px -22px rgba(168,132,47,0.5); }
.itc-root .thead { padding: 13px 16px; min-height: 64px; border-bottom: 2px solid var(--ink); display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.itc-root .thead h2 { font-size: 15px; font-weight: 600; line-height: 1.25; letter-spacing: -0.005em; }
.itc-root .thead .type { font-family: var(--itc-font); font-size: 10px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); white-space: nowrap; }
.itc-root .tacct.star .thead { background: var(--gold-tint); }
.itc-root .cols { display: grid; grid-template-columns: 1fr 1fr; min-height: 148px; }
.itc-root .col { padding: 10px 12px 12px; }
.itc-root .col + .col { border-left: 1px solid var(--line); }
.itc-root .col .c-lbl { font-family: var(--itc-font); font-size: 10px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); display: block; margin-bottom: 8px; }
.itc-root .entry { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; line-height: 1.4; padding: 3px 0; color: var(--ink-2); animation: itc-in 0.35s ease both; }
.itc-root .entry .e-lbl { color: var(--muted); font-size: 11px; }
.itc-root .entry b { font-weight: 600; font-variant-numeric: tabular-nums; }
.itc-root .entry.receipt b { color: var(--ink); }
.itc-root .entry.bill b { color: var(--teal); }
.itc-root.bad-mode .entry.bill b { color: var(--red); }
@keyframes itc-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
.itc-root .tfoot { border-top: 1px solid var(--line); padding: 11px 16px; display: flex; justify-content: space-between; align-items: baseline; background: var(--wash); }
.itc-root .tfoot .f-lbl { font-family: var(--itc-font); font-size: 10px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); }
.itc-root .tfoot .bal { font-size: 17px; font-weight: 600; font-variant-numeric: tabular-nums; transition: color 0.2s ease; }
.itc-root .tfoot .bal.ok { color: var(--teal); }
.itc-root .tfoot .bal.warn { color: var(--red); }
.itc-root .flag { font-size: 11.5px; line-height: 1.45; padding: 9px 16px 11px; border-top: 1px dashed var(--line); }
.itc-root .flag.warn { color: var(--red); background: var(--red-tint); border-top: 1px dashed var(--red-line); }
.itc-root .flag.ok { color: var(--teal); background: var(--teal-tint); border-top: 1px dashed var(--teal-line); }

/* ---- drift chart ---- */
.itc-root .drift { margin-top: 28px; border: 1px solid var(--line); border-radius: 10px; padding: 20px 22px 18px; background: var(--wash); }
.itc-root .drift .d-head { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; flex-wrap: wrap; }
.itc-root .drift .d-lbl { font-family: var(--itc-font); font-size: 11px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-2); }
.itc-root .drift .d-val { font-size: 14px; font-weight: 600; font-variant-numeric: tabular-nums; }
.itc-root .drift .d-val.ok { color: var(--teal); }
.itc-root .drift .d-val.warn { color: var(--red); }
.itc-root .bars { display: flex; align-items: flex-end; gap: 12px; height: 96px; margin-top: 16px; border-bottom: 2px solid #C6CDD2; }
.itc-root .bar-slot { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; align-items: center; height: 100%; }
.itc-root .bar { width: 100%; max-width: 72px; border-radius: 3px 3px 0 0; transition: height 0.4s ease, background 0.3s ease; }
.itc-root .bar.zero { height: 3px !important; background: var(--teal); border-radius: 2px; }
.itc-root .bar.grow { background: var(--red); }
.itc-root .bar-amt { font-family: var(--itc-font); font-size: 10.5px; font-weight: 600; color: var(--ink-2); margin-bottom: 5px; font-variant-numeric: tabular-nums; }
.itc-root .bar-x { display: flex; gap: 12px; margin-top: 7px; }
.itc-root .bar-x span { flex: 1; text-align: center; font-family: var(--itc-font); font-size: 10px; letter-spacing: 0.04em; color: var(--muted); }
.itc-root .drift .d-note { font-size: 12.5px; line-height: 1.5; color: var(--body); font-style: italic; margin-top: 14px; }

/* ---- final comparison ---- */
.itc-root .final { margin-top: 28px; border-radius: 10px; border: 1px solid var(--gold-line); background: var(--gold-tint); padding: 22px 24px; animation: itc-in 0.4s ease both; }
.itc-root .final .f2-lbl { font-family: var(--itc-font); font-size: 11px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--gold); }
.itc-root .final table { width: 100%; border-collapse: collapse; margin-top: 14px; }
.itc-root .final th { font-family: var(--itc-font); font-size: 10px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); text-align: right; padding: 6px 4px; border-bottom: 1px solid var(--gold-line); }
.itc-root .final th:first-child { text-align: left; }
.itc-root .final td { font-size: 13.5px; padding: 9px 4px; border-bottom: 1px solid rgba(168,132,47,0.15); text-align: right; font-variant-numeric: tabular-nums; color: var(--ink-2); }
.itc-root .final td:first-child { text-align: left; font-weight: 500; color: var(--ink); }
.itc-root .final td .good { color: var(--teal); font-weight: 600; }
.itc-root .final td .bad { color: var(--red); font-weight: 600; }
.itc-root .final td .same { color: var(--ink-2); font-weight: 600; }
.itc-root .final td small { display: block; font-size: 11px; color: var(--muted); font-weight: 400; margin-top: 1px; }
.itc-root .final .twist { font-size: 13px; line-height: 1.55; color: var(--ink-2); margin-top: 16px; }
.itc-root .final .twist b { font-weight: 600; color: var(--ink); }

.itc-root .foot { margin-top: 30px; padding-top: 16px; border-top: 1px solid var(--line-2); text-align: left; font-size: 12px; font-style: italic; color: var(--muted); }

@container (max-width: 1000px) {
  .itc-root { --pad: 28px; padding: 32px var(--pad) 26px; }
  .itc-root .head { max-width: 66%; }
}
@container (max-width: 640px) {
  .itc-root { --pad: 0px; padding: 26px var(--pad) 22px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
  .itc-root .head h2 { font-size: 24px; }
  .itc-root .sub { font-size: 13px; }
  .itc-root .itc-logo { display: none; }
  .itc-root .head { max-width: 100%; margin-top: 0; }
  .itc-root .toggle { display: flex; width: 100%; margin-top: 16px; }
  .itc-root .toggle button { flex: 1; justify-content: center; min-height: 44px; padding: 9px 10px; }
  .itc-root .rail { margin-top: 24px; }
  .itc-root .rail-actions { width: 100%; }
  .itc-root .rail .btn { width: 100%; justify-content: center; }
  .itc-root .rail .reset { align-self: flex-start; }
  .itc-root .pos { width: 100%; margin-left: 0; }
  .itc-root .po { flex: 1; text-align: center; padding: 6px 4px; font-size: 9px; }
  .itc-root .cols { min-height: 120px; }
  .itc-root .final { padding: 18px 16px; }
  .itc-root .final td { font-size: 12px; }
  .itc-root .taccts { padding: 4px var(--pad) 8px; gap: 14px; scroll-snap-type: x mandatory; scrollbar-width: none; }
  .itc-root .taccts::-webkit-scrollbar { display: none; }
  .itc-root .tacct, .itc-root .tacct.star { flex: 0 0 80%; }
  /* An empty debit/credit column is squeezed 30% so the populated side gets more room */
  .itc-root .cols.cr-empty { grid-template-columns: 1.3fr 0.7fr; }
  .itc-root .cols.dr-empty { grid-template-columns: 0.7fr 1.3fr; }
  .itc-root .bars { gap: 7px; }
  .itc-root .bar-x { gap: 7px; }
  .itc-root .foot { text-align: left; }
}
`;

function useInjectStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* --------------------------- Sub-components ---------------------------- */
interface TAccountProps {
  name: string;
  type: string;
  ledger: Ledger;
  star?: boolean;
  flag?: { tone: 'ok' | 'warn'; text: React.ReactNode } | null;
  balanceTone?: 'ok' | 'warn' | null;
}

const TAccount: React.FC<TAccountProps> = ({ name, type, ledger, star, flag, balanceTone }) => {
  const balance = Math.abs(sum(ledger.dr) - sum(ledger.cr));
  const colsClass = 'cols' + (ledger.cr.length === 0 ? ' cr-empty' : '') + (ledger.dr.length === 0 ? ' dr-empty' : '');
  return (
    <div className={'tacct' + (star ? ' star' : '')}>
      <div className="thead">
        <h2>{name}</h2>
        <span className="type">{type}</span>
      </div>
      <div className={colsClass}>
        <div className="col">
          <span className="c-lbl">Debit</span>
          {ledger.dr.map((e, i) => (
            <div className={'entry ' + e.kind} key={e.label + i}>
              <span className="e-lbl">{e.label}</span>
              <b>{fmt(e.amount)}</b>
            </div>
          ))}
        </div>
        <div className="col">
          <span className="c-lbl">Credit</span>
          {ledger.cr.map((e, i) => (
            <div className={'entry ' + e.kind} key={e.label + i}>
              <span className="e-lbl">{e.label}</span>
              <b>{fmt(e.amount)}</b>
            </div>
          ))}
        </div>
      </div>
      <div className="tfoot">
        <span className="f-lbl">Balance</span>
        <span className={'bal' + (balanceTone ? ' ' + balanceTone : '')}>{fmt(balance)}</span>
      </div>
      {flag && <div className={'flag ' + flag.tone}>{flag.text}</div>}
    </div>
  );
};

/* ------------------------------ Component ------------------------------ */
const InventoryTrapChart: React.FC = () => {
  useInjectStyles();
  const [mode, setMode] = useState<Mode>('linked');
  const [step, setStep] = useState<number>(1);

  /* Edge-fades: show a gentle white gradient only when there's off-screen content */
  const scrollRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<{ left: boolean; right: boolean }>({ left: false, right: false });
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  const bad = mode === 'standalone';
  const { inv, acc, ap } = buildLedgers(step, mode);
  const invBal = sum(inv.dr);
  const accBal = sum(acc.cr) - sum(acc.dr);
  const done = step === POS.length;
  const total = POS.reduce((t, p) => t + p.amount, 0);

  /* Accrued Purchases running balance after each PO's bill posts */
  const drift = POS.map((_po, i) =>
    bad ? POS.slice(0, i + 1).reduce((t, p) => t + p.amount, 0) : 0,
  );
  const maxDrift = total;

  const accFlag = bad
    ? accBal > 0
      ? { tone: 'warn' as const, text: <>The standalone bill never touches this account — the accrual is stranded.</> }
      : null
    : step > 0
      ? { tone: 'ok' as const, text: <>Each linked bill clears the accrual back to $0. This account self-audits.</> }
      : null;

  const invFlag =
    bad && step > 0
      ? { tone: 'warn' as const, text: <>Debited twice per PO — once at receipt, again by the bill. Inventory is 2× overstated.</> }
      : null;

  return (
    <div className="itc-wrap">
      <div className={'itc-root' + (bad ? ' bad-mode' : '')}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className="itc-logo" src={PSGLOBAL_LOGO} alt="PS Global" />
        <div className="head">
          <h2>The inventory item trap</h2>
          <p className="sub">What actually posts to the ledger when a bill is — and isn&rsquo;t — linked to its purchase order. Pick a posting mode, then click through the five POs and watch the accounts fill in.</p>
        </div>
        <div className="toggle" role="tablist" aria-label="Posting mode">
          <button className={!bad ? 'on good' : ''} onClick={() => setMode('linked')} role="tab" aria-selected={!bad}>
            <span className="dot"></span>Bill linked to PO
          </button>
          <button className={bad ? 'on bad' : ''} onClick={() => setMode('standalone')} role="tab" aria-selected={bad}>
            <span className="dot"></span>Standalone bill
          </button>
        </div>

        <div className="rail">
          <div className="rail-actions">
            <button className="btn" onClick={() => setStep(step + 1)} disabled={done}>
              {done ? 'All 5 POs posted' : `Click to post ${POS[step].id}`}
              {!done && <span className="tap" aria-hidden="true">{'→'}</span>}
            </button>
            <button className="reset" onClick={() => setStep(1)}>Reset</button>
          </div>
          <div className="pos">
            {POS.map((po, i) => (
              <span className={'po' + (i < step ? ' done' : '')} key={po.id}>
                {po.id}
                <b>{fmt(po.amount)}</b>
              </span>
            ))}
          </div>
        </div>

        <div className={'taccts-wrap' + (edges.left ? ' can-l' : '') + (edges.right ? ' can-r' : '')}>
        <div className="taccts" ref={scrollRef}>
          <TAccount
            name="Inventory Asset"
            type="Asset &middot; Dr"
            ledger={inv}
            flag={invFlag}
            balanceTone={bad ? 'warn' : null}
          />
          <TAccount
            name="Accrued Purchases"
            type="Liability &middot; Cr"
            ledger={acc}
            star
            flag={accFlag}
            balanceTone={bad ? 'warn' : 'ok'}
          />
          <TAccount name="Accounts Payable" type="Liability &middot; Cr" ledger={ap} />
        </div>
        </div>

        <div className="drift">
          <div className="d-head">
            <span className="d-lbl">Accrued Purchases &middot; balance after each bill posts</span>
            <span className={'d-val ' + (bad ? 'warn' : 'ok')}>
              {bad ? `Drift: ${fmt(accBal)} and climbing` : 'Drift: $0 — clears every cycle'}
            </span>
          </div>
          <div className="bars">
            {drift.map((v, i) => {
              const posted = i < step;
              return (
                <div className="bar-slot" key={POS[i].id}>
                  {posted && bad && <span className="bar-amt">{fmt(v)}</span>}
                  <div
                    className={'bar' + (posted ? (bad ? ' grow' : ' zero') : '')}
                    style={{ height: posted && bad ? `${Math.max(6, (v / maxDrift) * 78)}px` : '0px' }}
                  ></div>
                </div>
              );
            })}
          </div>
          <div className="bar-x">
            {POS.map((po) => (
              <span key={po.id}>{po.id}</span>
            ))}
          </div>
          <p className="d-note">
            {bad
              ? 'Nothing errors, nothing bounces — the balance just quietly climbs until someone catches it at audit.'
              : 'Spikes at receipt, snaps back to $0 when the linked bill posts. A healthy clearing account.'}
          </p>
        </div>

        {done && (
          <div className="final">
            <span className="f2-lbl">After all five POs &middot; side by side</span>
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Bill linked to PO</th>
                  <th>Standalone bill</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Inventory Asset</td>
                  <td><span className="good">{fmt(total)}</span></td>
                  <td><span className="bad">{fmt(total * 2)}</span><small>2&times; overstated</small></td>
                </tr>
                <tr>
                  <td>Accrued Purchases</td>
                  <td><span className="good">$0</span></td>
                  <td><span className="bad">{fmt(total)}</span><small>stranded</small></td>
                </tr>
                <tr>
                  <td>Accounts Payable</td>
                  <td><span className="same">{fmt(total)}</span></td>
                  <td><span className="same">{fmt(total)}</span><small>identical</small></td>
                </tr>
              </tbody>
            </table>
            <p className="twist">
              <b>Accounts Payable is identical in both modes.</b> Debits still equal credits, the trial balance ties, and the
              AP clerk sees a clean payables ledger. The damage sits in two accounts the AP team never opens — which is
              exactly why it survives until audit.
            </p>
          </div>
        )}

        <p className="foot">Amounts illustrative. Account names as in NetSuite: Accrued Purchases = Inventory Received Not Billed.</p>
      </div>
    </div>
  );
};

export default InventoryTrapChart;
